import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Logo } from './Logo';

/** How long the mark stays up before it starts to go, and how long the fade takes. */
const MIN_VISIBLE_MS = 2000;
const FADE_MS = 550;

const SEEN_KEY = 'happychopper:splash-seen';

/**
 * Once per visit, not once per refresh.
 *
 * sessionStorage rather than localStorage deliberately: opening the app should
 * still greet you, but reloading twenty times while you work should not. Closing
 * the tab resets it.
 */
export function splashWasSeen(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === '1';
  } catch {
    // Private mode with storage blocked — better to show it than to loop.
    return false;
  }
}

export function markSplashSeen(): void {
  try {
    sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* nothing to remember it with */
  }
}

/**
 * Opening splash.
 *
 * Doubles as the boot screen: session restore runs underneath it, so the
 * two-second hold is spent doing real work rather than stalling. It won't leave
 * until the restore has finished either, which means the app is never revealed
 * mid-load. Click or press anything to skip it.
 */
export function Splash({ ready, onDone }: { ready: boolean; onDone: () => void }) {
  const [leaving, setLeaving] = useState(false);

  // Hold for the minimum, then go — but only once there's something to reveal.
  useEffect(() => {
    if (!ready || leaving) return;
    const elapsed = Date.now() - start;
    const timer = window.setTimeout(
      () => setLeaving(true),
      Math.max(0, MIN_VISIBLE_MS - elapsed),
    );
    return () => window.clearTimeout(timer);
  }, [ready, leaving]);

  useEffect(() => {
    if (!leaving) return;
    const timer = window.setTimeout(onDone, FADE_MS);
    return () => window.clearTimeout(timer);
  }, [leaving, onDone]);

  // Skipping shouldn't strand you on a half-restored app, so it still waits
  // for `ready` — it just skips the rest of the hold.
  useEffect(() => {
    const skip = () => ready && setLeaving(true);
    window.addEventListener('keydown', skip);
    return () => window.removeEventListener('keydown', skip);
  }, [ready]);

  return (
    <div
      onClick={() => ready && setLeaving(true)}
      className={clsx(
        'fixed inset-0 z-[80] flex cursor-pointer flex-col items-center justify-center gap-4 bg-ink-950',
        leaving && 'fade-out',
      )}
    >
      <div className="zoom-in">
        <Logo size={84} />
      </div>

      <div className="rise flex flex-col items-center gap-1">
        <span className="display text-3xl text-ink-100">Happy Chopper</span>
        <span className="text-xs text-ink-400">
          by{' '}
          <a
            href="https://fintonlabs.com"
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
            className="text-volt-400 underline decoration-volt-500/40 underline-offset-2 hover:decoration-volt-400"
          >
            fintonlabs.com
          </a>
        </span>
      </div>
    </div>
  );
}

/** Module load is close enough to "app open" for a two-second hold. */
const start = Date.now();
